/**
 * OAuth callback: complete Clerk handshake, then call Django clerk_login and redirect.
 * Sign-in only (user must already exist in Clerk). No sign-up / no adding users to Clerk.
 * We redirect to ourselves (clean URL) after the handshake so Clerk's session is ready before getToken/clerk_login.
 */
import React, { useEffect, useRef } from 'react'
import { useClerk, useAuth } from '@clerk/clerk-react'
import { axiosInstance, LocalStorage } from '../../../../helpers'
import { resolvePostLoginRoute } from '../../../../helpers/loginNext'
import { LoadingScreen } from '../../../../navigation/LoadingScreen'

const CLERK_LOGIN_PATH =
    process.env.REACT_APP_CLERK_LOGIN_PATH || 'user/clerk_login/'
const GET_TOKEN_RETRY_MS = 400
const GET_TOKEN_MAX_ATTEMPTS = 25

const authCallbackUrl =
    typeof window !== 'undefined'
        ? `${window.location.origin}/auth-callback`
        : '/auth-callback'
const loginErrorUrl =
    typeof window !== 'undefined'
        ? `${window.location.origin}/login?error=invalid_email`
        : '/login?error=invalid_email'

function hasOAuthParams(): boolean {
    if (typeof window === 'undefined') return false
    const params = new URLSearchParams(window.location.search)
    return (
        params.has('__clerk_handshake') ||
        params.has('code') ||
        params.has('state')
    )
}

export const AuthCallback = () => {
    const { handleRedirectCallback } = useClerk()
    const { getToken } = useAuth()
    const doneRef = useRef(false)

    useEffect(() => {
        if (doneRef.current) return
        doneRef.current = true

        async function run() {
            if (hasOAuthParams()) {
                try {
                    await handleRedirectCallback({})
                } catch {
                    /**/
                }
                window.location.href = authCallbackUrl
                return
            }

            let clerkToken: string | null = null
            for (let i = 0; i < GET_TOKEN_MAX_ATTEMPTS; i++) {
                try {
                    clerkToken = await getToken()
                    if (clerkToken) break
                } catch {
                    /**/
                }
                await new Promise((r) => setTimeout(r, GET_TOKEN_RETRY_MS))
            }
            if (!clerkToken) {
                window.location.href = loginErrorUrl
                return
            }
            try {
                const { data: rootUser } = await axiosInstance.post(
                    CLERK_LOGIN_PATH,
                    { token: clerkToken, device: 'web' },
                    { headers: { 'Content-Type': 'application/json' } },
                )
                const workspace = rootUser?.workspaces?.[0]
                const token = workspace?.token ?? rootUser?.token
                if (token && workspace) {
                    localStorage.setItem(LocalStorage.EZTURN4_TOKEN, token)
                    axiosInstance.defaults.headers.common[
                        'Authorization'
                    ] = `token ${token}`
                    window.location.href = resolvePostLoginRoute(
                        workspace.active_workspace?.company_type,
                    )
                } else {
                    window.location.href = loginErrorUrl
                }
            } catch {
                window.location.href = loginErrorUrl
            }
        }

        run()
    }, [handleRedirectCallback, getToken])

    return (
        <LoadingScreen
            title="Signing you in…"
            subtitle="Setting up your workspace"
        />
    )
}
